import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { SimpleChanges } from '@angular/core';
import {
  BannerTypes,
  MessageBannerConfig,
} from '@zipari/shared-ds-util-messages';
import { cloneObject } from '@zipari/web-utils';
import {
  PreferenceColumn,
  PreferenceData,
  PreferenceGridConfig,
  PreferenceOptionsType,
} from './preferences-grid.model';
import { checkInputsForText } from '../../design-system.helper';

@Component({
  selector: 'preference-grid',
  templateUrl: './preferences-grid.component.html',
  styleUrls: ['./preferences-grid.component.scss'],
})
export class PreferenceGridComponent implements OnChanges, OnInit {
  @Input() config: PreferenceGridConfig;
  @Input() someOptionsSelected: boolean;
  @Output() someOptionsSelectedChange: EventEmitter<Record<number, boolean>> =
    new EventEmitter();
  @Output() editedValue: EventEmitter<PreferenceData> = new EventEmitter();

  formGroup: UntypedFormGroup = new UntypedFormGroup({});
  formType: typeof PreferenceOptionsType = PreferenceOptionsType;
  isCorrectAPI: boolean;
  errorMessageConfig: MessageBannerConfig;
  isPreference: boolean;

  ngOnInit(): void {
    this.checkForDisabledColumns();
    this.config.rows = cloneObject(this.config.rows);
    this.errorMessageConfig = {
      type: BannerTypes.error,
      message: 'Error: the grid should not have multiple selection options',
      icon: 'warning',
    };
    this.checkDiffTypesInColumns();
  }

  checkDiffTypesInColumns(): void {
    const allColumnTypes = [];
    // eslint-disable-next-line array-callback-return

    this.config.columns.map((column) => {
      allColumnTypes.push(column.type);
    });
    this.isCorrectAPI = !this.checkDiffTypes(allColumnTypes);
  }

  checkDiffTypes(arr: string[]): boolean {
    return arr.some((el) => el !== arr[0]);
  }

  checkForDisabledColumns(): void {
    this.config.columns = this.config.columns.map((col: PreferenceColumn) => ({
      ...col,
      disabled: !this.checkValidCommunication(col.short_name) || col.disabled,
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    const [config] = checkInputsForText([this.config]);

    this.config = config;

    const { currentValue, previousValue, firstChange } = changes.config || {};

    if (firstChange || currentValue !== previousValue) {
      this.formatPayload();
    }
  }

  checkValidCommunication(fieldToCheck: string) {
    if (!this.config.commStats || fieldToCheck === 'Mail') {
      return true;
    }

    const field = fieldToCheck.toLowerCase();

    if (field === 'text' || /phone/.test(field)) {
      return this.config.commStats['cell_phone']?.length > 0;
    }

    if (/mail/.test(field)) {
      return this.config.commStats['email_address']?.length > 0;
    }

    return true;
  }

  public showCheckbox(column: PreferenceColumn): boolean {
    return column.type === 'multi_select' || !column.type;
  }

  toggleCheck(column, row) {
    if (row.user_selected_option_ids.includes(column.id)) {
      row.user_selected_option_ids = [
        ...row.user_selected_option_ids.filter((id) => id !== column.id),
      ];
    } else {
      row.user_selected_option_ids = [
        ...row.user_selected_option_ids,
        column.id,
      ];
    }
  }

  toggleCell(column, row) {
    if (column.disabled) {
      return;
    }

    // const options = row.user_selected_option_ids;

    if (column.type === PreferenceOptionsType.multiSelect) {
      this.toggleCheck(column, row);
    }

    if (column.type === PreferenceOptionsType.exclusiveSelect) {
      row.user_selected_option_ids = [];
      this.toggleCheck(column, row);
    }

    this.formatPayload();
  }

  toggleAll(column, rows): void {
    if (column.disabled) {
      return;
    }
    if (this.isColumnSelected(column, rows)) {
      rows.forEach((row) => {
        this.toggleCell(column, row);
      });
    } else {
      rows.forEach((row) => {
        if (!this.isCellSelected(column, row)) {
          this.toggleCell(column, row);
        }
      });
    }
  }

  isColumnSelected(column, rows): boolean {
    return rows.every((row) => this.isCellSelected(column, row));
  }

  isCellSelected(column, row): boolean {
    return row.user_selected_option_ids.includes(column.id);
  }

  anyOptionsSelected(): boolean {
    return this.config.rows.some((row) => row.user_selected_option_ids.length);
  }

  formatPayload() {
    const optionsReference = {
      [this.config.data?.id]: this.anyOptionsSelected(),
    };

    this.someOptionsSelectedChange.emit(optionsReference);
    this.editedValue.emit({
      ...this.config.data,
      preference_types: this.config.isEnrolled
        ? this.config.rows
        : this.config.rows.map((row) => ({
            ...row,
            user_selected_option_ids: [],
          })),
    });
    this.checkForDisabledColumns();
  }
}
